import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight, Shield, ArrowLeft } from "lucide-react";
import { ProgressSteps } from "./submit-project/components/ProgressSteps";
import { RequirementsForm } from "./submit-project/components/RequirementsForm";
import { FileUploadForm } from "./submit-project/components/FileUploadForm";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { sanitizeInput } from "@/utils/security";
import { useJobStore } from "@/store/jobStore";
import { ProjectDetailsForm } from "./submit-project/components/ProjectDetailsForm";
import { useUserStore } from "@/store/userStore";
import LoaderOverlay from "@/components/common/LoaderOverlay";
import AILoader from "@/components/common/AILoader";
import CircularDotsLoader from "@/components/common/CircularLoader";

// Define Zod schemas for each step
const projectDetailsSchema = z.object({
  title: z.string().min(1, "Title is required").max(100, "Title is too long"),
  description: z
    .string()
    .min(1, "Description is required")
    .max(5000, "Description is too long"),
  category: z.string().min(1, "Category is required"),
});

const requirementsSchema = z.object({
  budget: z
    .number()
    .min(1, "Budget is required"),
  durationValue: z.number().min(1, "Duration is required"),
  durationUnit: z.string().optional(),
  PricingDetails: z.enum(["fixedPrice", "dailyRate"], {
    required_error: "Pricing detail is required",
  }),
  experienceLevel: z.enum(["entry", "intermediate", "expert"], {
    required_error: "Experience level is required",
  }),
  skills: z.array(z.string()).min(1, "At least one skill is required"),
});

const fileUploadSchema = z.object({
  projectVisibility: z.enum(["public", "private"], {
    required_error: "Visibility is required",
  }),
  files: z.array(z.any()).optional(),
});

const projectSchema = projectDetailsSchema
  .merge(requirementsSchema)
  .merge(fileUploadSchema);

type ProjectFormData = z.infer<typeof projectSchema>;

const SubmitProject = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { createJobProfile, updateJobProfile, userJobs } = useJobStore();
  const { user } = useUserStore();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

  // Initialize react-hook-form with zod resolver
  const methods = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      title: "",
      description: "",
      budget: 0,
      durationValue: 1,
      category: "",
      durationUnit: "1",
      PricingDetails: "fixedPrice",
      experienceLevel: "intermediate",
      skills: [],
      projectVisibility: "public",
      files: [],
    },
    mode: "onChange",
  });

  const { handleSubmit, setValue, watch, trigger } = methods;
  const skills = watch("skills");
  const files = watch("files") || [];

  // Detect edit mode via query param
  const searchParams = new URLSearchParams(location.search);
  const editId = searchParams.get("editId");

  // Prefill form in edit mode
  useEffect(() => {
    if (!editId || !Array.isArray(userJobs)) return;
    const job = userJobs.find((j: any) => j._id === editId);
    if (!job) return;
    setValue("title", job.title || "");
    setValue("description", job.description || "");
    setValue("category", job.category || "");
    setValue("budget", Number(job.budget) || 0);
    setValue("durationValue", Number(job.durationValue) || 1);
    setValue("durationUnit", String(job.durationUnit ?? "1"));
    setValue(
      "PricingDetails",
      (job.PricingDetails === "dailyRate" ? "dailyRate" : "fixedPrice") as "fixedPrice" | "dailyRate"
    );
    setValue(
      "experienceLevel",
      (job.experienceLevel === "entry" || job.experienceLevel === "expert" ? job.experienceLevel : "intermediate") as "entry" | "intermediate" | "expert"
    );
    setValue("skills", job.requiredSkills || []);
    setValue("projectVisibility", job.projectvisibility ? "public" : "private");
  }, [editId, userJobs, setValue]);

  const stepFields: { [key: number]: (keyof ProjectFormData)[] } = {
    1: ["title", "description", "category"],
    2: ["budget", "durationValue", "PricingDetails", "experienceLevel", "skills"],
    3: ["projectVisibility"],
  };

  const handleNextStep = async () => {
    const isValid = await trigger(stepFields[step] as any, {
      shouldFocus: true,
    });
    if (isValid) {
      setStep((prev) => prev + 1);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.add("border-emerald-500");
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove("border-emerald-500");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove("border-emerald-500");
    const droppedFiles = Array.from(e.dataTransfer.files);
    setValue("files", [...files, ...droppedFiles]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setValue("files", [...files, ...Array.from(e.target.files)]);
    }
  };

  const removeFile = (index: number) => {
    setValue(
      "files",
      files.filter((_: any, i: number) => i !== index)
    );
  };

  const handleAddSkill = (newSkill: string) => {
    if (newSkill.trim() && !skills.includes(newSkill)) {
      const newSkills = newSkill
        .split(",")
        .map((skill) => skill.trim())
        .filter((skill) => skill.length > 0 && !skills.includes(skill));

      if (newSkills.length > 0) {
        setValue("skills", [...skills, ...newSkills]);
      }
    } else if (skills.includes(newSkill)) {
      toast({
        title: "Skill already added",
        description: "This skill is already in your list.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveSkill = (skill: string) => {
    setValue(
      "skills",
      skills.filter((s: string) => s !== skill)
    );
  };

  const onSubmit = async (data: ProjectFormData) => {
    try {
      const projectData: any = {
        ...data,
        durationValue: +data.durationValue,
        durationUnit: +data.durationUnit,
        status: "open",
        userId: user?.userId,
        requiredSkills: data.skills,
      };

      Object.keys(projectData).forEach((key) => {
        if (typeof projectData[key] === "string") {
          projectData[key] = sanitizeInput(projectData[key]);
        }
      });
      if (editId) {
        await updateJobProfile(editId, projectData, user?.accessToken);
        toast({ title: "Job updated successfully", variant: "default" });
      } else {
        await createJobProfile(projectData, user?.accessToken);
        toast({
          title: "Project Submitted Successfully!",
          description: "We'll review your project and notify you soon.",
          variant: "default",
        });
      }
      navigate("/job-list");
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: "An error occurred while submitting your project.",
        variant: "destructive",
      });
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return <ProjectDetailsForm setIsLoading={setIsLoading} />;
      case 2:
        return (
          <RequirementsForm
            skills={skills}
            onAddSkill={handleAddSkill}
            onRemoveSkill={handleRemoveSkill}
          />
        );
      case 3:
        return (
          <FileUploadForm
            files={files}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onFileChange={handleFileChange}
            onRemoveFile={removeFile}
          />
        );
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 1:
        return "Job Details";
      case 2:
        return "Requirements & Timeline";
      case 3:
        return "Files & Visibility";
      default:
        return "";
    }
  };

  return (
    <>
    {isLoading &&  <CircularDotsLoader className="absolute" text="AI is scanning your job document to auto-fill information..." />}
    {/* {isLoading && <LoaderOverlay />} */}
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-bold text-gradient mb-4">
        {editId ? "Edit Job" : "Create a New Job"}
      </h1>
      <p className="text-gray-600 mb-8">
        {editId
          ? "Update your job details and save changes."
          : "Fill out the form below to post your job and find the perfect talent."}
      </p>

      <ProgressSteps currentStep={step} />

      <Card className="border-blue-100 glass-effect">
        <CardHeader>
          <CardTitle className="text-2xl text-gradient flex items-center">
            {getStepTitle()}
            <Shield className="ml-2 w-5 h-5 text-emerald-500" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FormProvider {...methods}>
            <form onSubmit={(e) => {
                e.preventDefault();
                if (step === 4) {
                  handleSubmit(onSubmit)(e);
                }
              }}>
              {renderStepContent()}

              <div className="flex justify-between mt-8">
                {step > 1 && (
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => setStep((prev) => prev - 1)}
                    className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                )}
                <Button
                  type={step === 4 ? "submit" : "button"}
                  className={`bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 ml-auto`}
                  onClick={step < 4 ? handleNextStep : undefined}
                >
                  {step < 3 ? (
                    <>
                      Next Step
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  ) : (
                    <>
                      Submit Project
                    </>
                  )}
                </Button>
              </div>
            </form>
          </FormProvider>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default SubmitProject;